import React, { useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import {
  Button,
  FilterConfig,
  FilterType,
  Table,
  TableFilter,
  useTable,
  useFilters,
  Flex,
  BoxV2 as Box,
} from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faFilter } from '@fortawesome/pro-regular-svg-icons';

import { globalGetService } from '../../../../../utils/globalApiServices';
import CampaignListingRow from '../CampaignListingRow';
import { CampaignDetail } from '../../../Campaigns/types';
import { SelectItem } from '../../../../../utils/types';
import { getRcsUseCaseOptions, getTenDlcUseCaseOptions } from '@/apis/services';

interface UseCase {
  id: string;
  uid?: string;
  displayName: string;
}

const defaultFilterConfigs: Record<string, FilterConfig> = {
  usecase: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Use-Case',
    placeholder: 'select use-case',
    width: 230,
  },
  description: {
    type: FilterType.Text,
    label: 'Campaign Description',
    placeholder: 'campaign description',
    width: 230,
  },
  resellerName: {
    type: FilterType.Text,
    label: 'Reseller Name',
    placeholder: 'Enter reseller name',
    suggestions: [],
    width: 230,
  },
};
const defaultFilter = {
  sortField: 'createDate',
  ascendingOrder: false,
  recordsPerPage: 5,
};
const headRows = [
  { id: 'uid', label: 'CAMPAIGN ID', sortable: true },
  { id: 'usecase', label: 'USE CASE', sortable: true },
  { id: 'createDate', label: 'REGISTERED ON', sortable: true },
  { id: 'description', label: 'CAMPAIGN DESCRIPTION', sortable: false },
];

const fetchResellerSuggestions = async (query = {}) => {
  const response = await globalGetService('csp/reseller/suggestion', {
    ...query,
    limit: 20,
  });
  if (response && response?.ok) {
    return response.data.map((d: { companyName: string }) => d?.companyName);
  }
  return [];
};

const FILTER_LOADERS = {
  suggestion: {
    resellerName: {
      minLength: 2,
      load: (prefix: string) =>
        fetchResellerSuggestions({ companyNamePrefix: prefix }),
    },
  },
};

interface Props {
  brandUid: string;
  brandStatus?: string;
}

const BrandRegisteredCampaigns: React.FC<Props> = ({
  brandUid,
  brandStatus,
}) => {
  const [filtersShown, setFiltersShown] = useState(false);
  const {
    configs: filterConfigs,
    setConfig: setFilterConfigs,
    additionalFilters,
    appliedFilters,
    candidateFilters,
    handleApply: handleAppliedFiltersChange,
    handleEdit: handleCandidateFiltersChange,
    setOptions: setFilterOptions,
  } = useFilters({
    configs: defaultFilterConfigs,
    loaders: FILTER_LOADERS,
  });

  const table = useTable<CampaignDetail>(
    async (tableFilter) => {
      const response = await globalGetService('csp/campaigns', {
        ...tableFilter,
        ...appliedFilters,
        ...additionalFilters,
        rowsPerPage: defaultFilter.recordsPerPage,
        status: brandStatus === 'DELETED' ? 'EXPIRED' : 'ACTIVE',
        brandUid,
      }).then((res) => res?.data);

      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        page: tableFilter?.page || 1,
        rowsPerPage:
          typeof tableFilter?.recordsPerPage === 'number'
            ? tableFilter.recordsPerPage
            : defaultFilter.recordsPerPage,
      };
    },
    { deps: [appliedFilters, additionalFilters], defaultFilter }
  );

  const getAllUseCases = async () => {
    const [tenDlcOptions, rcsOptions] = await Promise.all([
      getTenDlcUseCaseOptions(),
      getRcsUseCaseOptions(),
    ]);
    const options = [...tenDlcOptions, ...rcsOptions].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    setFilterOptions('usecase', options);
  };

  const handleTableFilterToggle = () => {
    setFiltersShown(!filtersShown);
  };

  useEffect(() => {
    getAllUseCases();
  }, []);

  return (
    <Grid item xs={12} data-testid="brandRegisteredCampaigns">
      <div className="campaigns-listing details">
        <div className="details-heading campaigns-listing">
          <div className="title">
            <Flex sx={{ alignItems: 'center' }}>
              <Box sx={{ mr: 'xs' }}>
                <FontAwesomeIcon icon={faBullhorn} size="xl" />
              </Box>
              <h3 className="heading1">{`${table.total} Registered Campaigns`}</h3>
            </Flex>
          </div>
        </div>
        <Flex sx={{ marginBottom: '12px', alignItems: 'center', gap: '12px' }}>
          <Button
            variant="standard"
            color="secondary"
            onClick={handleTableFilterToggle}
            data-testid="tableAddFilterButton"
          >
            <FontAwesomeIcon icon={faFilter} />
            <span>{filtersShown ? 'Hide' : 'Show'} Filters</span>
          </Button>
        </Flex>
        {filtersShown && (
          <Grid container style={{ marginBottom: '12px' }}>
            <TableFilter
              configs={filterConfigs}
              candidateValues={candidateFilters}
              appliedValues={appliedFilters}
              onCandidateValuesChange={handleCandidateFiltersChange}
              onAppliedValuesChange={handleAppliedFiltersChange}
              data-testid="brandRegisteredCampaigns"
            />
          </Grid>
        )}
        <Table
          loading={table.loading}
          emptyState="no campaigns to view"
          handleChangePage={table.handleChangePage}
          createSortHandler={table.handleSorting}
          filter={table.filter}
          headRows={headRows}
          tableData={table.tableData.map((record) => {
            return <CampaignListingRow campaign={record} key={record.uid} />;
          })}
          pagination={table.pagination}
          testId="brandRegisteredCampaignListingTable"
          emptyBlockHeight="180px"
        />
      </div>
    </Grid>
  );
};

export default BrandRegisteredCampaigns;
